/**
 * TemplatePerformancePage — Measure pillar analytics surface.
 *
 * Per audit deliverable 06E. Kind-split per-template rankings: open/click rates,
 * send volume, status. Read-only; deep-links to the Marketing → Templates
 * management surface for edits.
 *
 * Data source:
 *   - GET /api/admin/email-analytics/overview?period=<today|Nd|custom&from&to>
 *     → data.templatePerformance: Array<{
 *         id, template_name, category, status, times_sent, open_rate, click_rate
 *       }>
 *     → data.summary: { total_templates, total_sent, avg_open_rate }
 */

import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Skeleton,
  Alert,
  Button,
  Chip,
  Divider,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import PillarPageShell from "../shared/PillarPageShell";
import TimeRangeFilter from "../../common/TimeRangeFilter";
import useTimeRangeParam from "../../common/useTimeRangeParam";
import { emailAnalyticsQuery } from "../shared/emailAnalyticsRange";
import apiService from "../../../services/api";

interface TemplateRow {
  id: string | number;
  template_name: string | null;
  category: string | null;
  status: string | null;
  times_sent: number | null;
  open_rate: number | null;
  click_rate: number | null;
}

interface TemplateSummary {
  total_templates: number | null;
  total_sent: number | null;
  avg_open_rate: number | null;
}

interface OverviewPayload {
  templatePerformance?: TemplateRow[];
  summary?: TemplateSummary;
}

// ── Formatting helpers ───────────────────────────────────────────────────────

const fmtInt = (n: number | null | undefined): string =>
  n != null && Number.isFinite(n) ? Math.round(n).toLocaleString() : "—";

const fmtPct = (n: number | null | undefined): string =>
  n != null && Number.isFinite(n) ? `${n.toFixed(1)}%` : "—";

/** Color-code a rate against open-rate benchmarks (good ≥ 25%, ok ≥ 15%). */
const openRateColor = (n: number | null | undefined): string => {
  if (n == null || !Number.isFinite(n)) return "text.secondary";
  if (n >= 25) return "#146152"; // teal — strong
  if (n >= 15) return "#D4AC0D"; // gold — acceptable
  return "#7D1935"; // wine — weak
};

/** Color-code a click rate (good ≥ 5%, ok ≥ 2%). */
const clickRateColor = (n: number | null | undefined): string => {
  if (n == null || !Number.isFinite(n)) return "text.secondary";
  if (n >= 5) return "#146152";
  if (n >= 2) return "#D4AC0D";
  return "#7D1935";
};

const statusChipColor = (
  status: string | null,
): "success" | "default" | "warning" => {
  const s = (status ?? "").toLowerCase();
  if (s === "active" || s === "live" || s === "published") return "success";
  if (s === "draft" || s === "paused" || s === "inactive") return "warning";
  return "default";
};

const TemplatePerformancePage: React.FC = () => {
  const navigate = useNavigate();
  const [period, setPeriod] = useTimeRangeParam({ type: "30d" });
  const [rows, setRows] = useState<TemplateRow[]>([]);
  const [summary, setSummary] = useState<TemplateSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await apiService.get<OverviewPayload>(
          `/api/admin/email-analytics/overview?${emailAnalyticsQuery(period)}`,
        );
        if (!mounted) return;
        // apiService.get unwraps the backend `data` envelope, so res.data is
        // the overview object itself. Tolerate a few nesting shapes defensively.
        const payload: OverviewPayload =
          (res?.data as any)?.data ?? (res?.data as OverviewPayload) ?? {};
        setRows(
          Array.isArray(payload.templatePerformance)
            ? payload.templatePerformance
            : [],
        );
        setSummary(payload.summary ?? null);
      } catch (e: any) {
        if (mounted) setError(e?.message ?? "Failed to load template performance");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [period]);

  // Sort by send volume, descending. Nulls sink to the bottom.
  const sortedRows = [...rows].sort(
    (a, b) => (b.times_sent ?? -1) - (a.times_sent ?? -1),
  );

  return (
    <PillarPageShell
      pillar="measure"
      title="Template Performance"
      subtitle="Per-template rankings by send volume with open & click rates. Read-only — deep-link to Templates to edit."
      rightActions={
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          <TimeRangeFilter value={period} onChange={setPeriod} />
          <Button
            variant="outlined"
            size="small"
            onClick={() =>
              navigate("/dashboard/communications/marketing/templates")
            }
            sx={{
              borderColor: "#1A5F7A",
              color: "#1A5F7A",
              "&:hover": { borderColor: "#134A60", bgcolor: "rgba(26,95,122,0.04)" },
            }}
          >
            Manage →
          </Button>
        </Box>
      }
    >
      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* KPI cards */}
      {loading ? (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {Array.from({ length: 3 }).map((_, i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ borderRadius: 2, borderLeft: "6px solid #1A5F7A" }}>
              <CardContent>
                <Typography variant="overline" sx={{ color: "text.secondary" }}>
                  Total Templates
                </Typography>
                <Typography variant="h4" sx={{ color: "#1A5F7A", mt: 0.5, fontWeight: 700 }}>
                  {/* `?? rows.length` manufactured a count from whatever
                      happened to be in the table when the summary was missing.
                      Unknown renders '—'. */}
                  {fmtInt(summary?.total_templates)}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {/* This count is not filtered by the period toggle. */}
                  all time
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ borderRadius: 2, borderLeft: "6px solid #5D6D7E" }}>
              <CardContent>
                <Typography variant="overline" sx={{ color: "text.secondary" }}>
                  Total Sent
                </Typography>
                <Typography variant="h4" sx={{ color: "#1A5F7A", mt: 0.5, fontWeight: 700 }}>
                  {fmtInt(summary?.total_sent)}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {/* total_sent counts SENDS. "delivered" is a different,
                      smaller number (Brevo's) and was never what this showed. */}
                  emails sent
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                borderRadius: 2,
                borderLeft: `6px solid ${openRateColor(summary?.avg_open_rate)}`,
              }}
            >
              <CardContent>
                <Typography variant="overline" sx={{ color: "text.secondary" }}>
                  Avg Open Rate
                </Typography>
                <Typography
                  variant="h4"
                  sx={{ color: openRateColor(summary?.avg_open_rate), mt: 0.5, fontWeight: 700 }}
                >
                  {fmtPct(summary?.avg_open_rate)}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  across all templates
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Templates table */}
      <Card sx={{ borderRadius: 2 }}>
        <CardContent sx={{ pb: 1 }}>
          <Typography
            variant="overline"
            sx={{ color: "text.secondary", display: "block" }}
          >
            Templates by send volume
          </Typography>
        </CardContent>
        <Divider />

        {loading ? (
          <Box sx={{ p: 2 }}>
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} height={44} sx={{ mb: 0.5 }} />
            ))}
          </Box>
        ) : sortedRows.length === 0 ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              No template performance data for this period yet.
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, color: "#1A5F7A" }}>
                    Template
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: "#1A5F7A" }}>
                    Category
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: "#1A5F7A" }}>
                    Sends
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: "#1A5F7A" }}>
                    Open Rate
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: "#1A5F7A" }}>
                    Click Rate
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: "#1A5F7A" }}>
                    Status
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedRows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {row.template_name ?? "—"}
                    </TableCell>
                    <TableCell>
                      {row.category ? (
                        <Chip
                          label={row.category}
                          size="small"
                          variant="outlined"
                          sx={{ textTransform: "capitalize" }}
                        />
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell align="right">{fmtInt(row.times_sent)}</TableCell>
                    <TableCell
                      align="right"
                      sx={{ color: openRateColor(row.open_rate), fontWeight: 600 }}
                    >
                      {fmtPct(row.open_rate)}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ color: clickRateColor(row.click_rate), fontWeight: 600 }}
                    >
                      {fmtPct(row.click_rate)}
                    </TableCell>
                    <TableCell>
                      {row.status ? (
                        <Chip
                          label={row.status}
                          size="small"
                          color={statusChipColor(row.status)}
                          sx={{ textTransform: "capitalize" }}
                        />
                      ) : (
                        "—"
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>
    </PillarPageShell>
  );
};

export default TemplatePerformancePage;
